import {
  BarChart3, BedDouble, BookOpen, CalendarCheck2, CalendarDays, CalendarHeart, ClipboardCheck, ClipboardList, Coins, CreditCard, Crown, FileBadge2, FileSignature, FileText, Gift,
  GraduationCap, HeartPulse, History, Home, Image, Inbox, LayoutDashboard, ListChecks, MapPin, Megaphone, MessageSquareQuote, Package, PartyPopper, Percent, QrCode, Receipt, Route, ScrollText,
  Settings, Share2, ShieldCheck, Sparkles, Store, Tag, Timer, Users, Warehouse, type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Shown when the user holds any of these permissions. */
  permission?: string | string[];
  /** Module feature flag that must be on for the stable. */
  feature?: string;
  badge?: ReactNode;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    label: 'Today',
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard },
      { label: 'Schedule', to: '/schedule', icon: CalendarDays, permission: 'booking.session.read', feature: 'booking' },
      { label: 'Check-in', to: '/check-in', icon: QrCode, permission: 'booking.booking.check_in', feature: 'booking' },
      { label: 'Inbox', to: '/inbox', icon: Inbox, permission: 'messaging.conversation.read', feature: 'messaging' },
      { label: 'Tasks', to: '/tasks', icon: ListChecks, permission: 'staff.task.read', feature: 'staff' },
    ],
  },
  {
    label: 'Bookings',
    items: [
      { label: 'Bookings', to: '/bookings', icon: CalendarCheck2, permission: 'booking.booking.read', feature: 'booking' },
      { label: 'Waitlist', to: '/waitlist', icon: Timer, permission: 'waitlist.entry.read', feature: 'waitlist' },
      { label: 'Services', to: '/services', icon: Store, permission: 'booking.service.read', feature: 'booking' },
      { label: 'Recurring schedules', to: '/session-templates', icon: History, permission: 'booking.session.read', feature: 'booking' },
      { label: 'Events', to: '/events', icon: PartyPopper, permission: 'events.event.read', feature: 'events' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { label: 'People', to: '/people', icon: Users, permission: 'people.person.read' },
      { label: 'Memberships', to: '/memberships', icon: Crown, permission: 'membership.membership.read', feature: 'membership' },
      { label: 'Membership plans', to: '/membership-plans', icon: Sparkles, permission: 'membership.plan.read', feature: 'membership' },
      { label: 'Packages', to: '/packages', icon: Package, permission: 'packages.package.read', feature: 'packages' },
      { label: 'Offers', to: '/offers', icon: Percent, permission: 'packages.offer.read', feature: 'packages' },
      { label: 'Gift cards', to: '/gift-cards', icon: Gift, permission: 'packages.gift_card.read', feature: 'gift_cards' },
      { label: 'Referrals', to: '/referrals', icon: Share2, permission: 'referral.referral.read', feature: 'referral' },
      { label: 'Waivers', to: '/waiver-templates', icon: FileSignature, permission: 'waivers.template.read', feature: 'waivers' },
      { label: 'Reviews', to: '/reviews', icon: MessageSquareQuote, permission: 'reviews.review.read', feature: 'reviews' },
    ],
  },
  {
    label: 'Horses & care',
    items: [
      { label: 'Horses', to: '/horses', icon: Sparkles, permission: 'horses.horse.read' },
      { label: 'Health due', to: '/health', icon: HeartPulse, permission: 'health.record.read', feature: 'health' },
      { label: 'History documents', to: '/history-documents', icon: FileBadge2, permission: 'horse_history.document.read', feature: 'horse_history' },
      { label: 'Training', to: '/skill-levels', icon: GraduationCap, permission: 'training.progress.read', feature: 'training' },
      { label: 'Tracked rides', to: '/rides', icon: Route, permission: 'ride_tracking.ride.read', feature: 'ride_tracking' },
      { label: 'Boarding', to: '/boarding', icon: BedDouble, permission: 'boarding.contract.read', feature: 'boarding' },
    ],
  },
  {
    label: 'Yard',
    items: [
      { label: 'Stalls', to: '/stalls', icon: Home, permission: 'staff.task.read', feature: 'staff' },
      { label: 'Rota', to: '/shifts', icon: ClipboardList, permission: 'staff.shift.read', feature: 'staff' },
      { label: 'Task templates', to: '/task-templates', icon: ClipboardCheck, permission: 'staff.template.manage', feature: 'staff' },
      { label: 'Inventory', to: '/inventory-items', icon: Warehouse, permission: 'inventory.item.read', feature: 'inventory' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Orders', to: '/orders', icon: Receipt, permission: 'payments.order.read' },
      { label: 'Payments', to: '/payments', icon: CreditCard, permission: 'payments.payment.read' },
      { label: 'Invoices', to: '/invoices', icon: FileText, permission: 'payments.invoice.read' },
    ],
  },
  {
    label: 'Growth',
    items: [
      { label: 'Analytics', to: '/analytics', icon: BarChart3, permission: 'analytics.dashboard.read', feature: 'analytics' },
      { label: 'Announcements', to: '/announcements', icon: Megaphone, permission: 'notifications.announcement.manage', feature: 'announcements' },
      { label: 'Occasions', to: '/occasion-rules', icon: CalendarHeart, permission: 'occasions.rule.read', feature: 'occasions' },
      { label: 'Website', to: '/website', icon: Image, permission: ['cms.page.manage', 'cms.blog.manage', 'cms.faq.manage', 'cms.album.manage'], feature: 'cms' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Settings', to: '/settings', icon: Settings, permission: 'tenancy.settings.read' },
      { label: 'Team & roles', to: '/team', icon: ShieldCheck, permission: 'identity.role.read' },
      { label: 'Locations', to: '/locations', icon: MapPin, permission: 'tenancy.location.read' },
      { label: 'Notifications', to: '/notifications', icon: BookOpen, permission: 'notifications.inbox.read' },
      { label: 'Audit log', to: '/audit-log', icon: ScrollText, permission: 'audit.log.read' },
    ],
  },
];

export const PLATFORM_NAV: NavItem[] = [
  { label: 'Stables', to: '/platform', icon: Store },
  { label: 'Feature flags', to: '/platform/flags', icon: Tag },
  { label: 'Operations', to: '/platform/ops', icon: Coins },
];
